/**
 * User Master Key (UMK) — generation + multi-envelope wrap/unwrap.
 *
 * The UMK is a 256-bit random secret generated once at enrolment. It is wrapped
 * independently by each KEK (passphrase / recovery / device / mitid-reserved)
 * into its own envelope row — the multi-envelope design that makes adding a new
 * unlock factor additive (INV-13) and passphrase rotation envelope-only (INV-14).
 *
 * Wrap/unwrap delegate to `envelope.ts` (XChaCha20-Poly1305). This file keeps
 * `wrapUmk`/`unwrapUmk`/`rewrapEnvelope`/`addEnvelope` together so the
 * "no item byte mutates when envelopes change" invariant cannot be bypassed.
 */
import { wrapEnvelope, openEnvelope } from './envelope.js';
import { randomBytes } from './random.js';
import { wipe } from './memory.js';
import { asUmk } from './types.js';
import { DekSerializationError } from './errors.js';
const UMK_BYTES = 32;
function trap() {
    throw new DekSerializationError();
}
function brandUmk(raw) {
    // Serialisation tripwire (R3): any JSON.stringify of a wrapper object throws.
    Object.defineProperty(raw, 'toJSON', { value: trap, enumerable: false });
    return asUmk(raw);
}
/** Generate a fresh 256-bit UMK. */
export async function generateUmk() {
    return brandUmk(randomBytes(UMK_BYTES));
}
/** Wrap the UMK under `kek` into an envelope blob (one row per kind). */
export async function wrapUmk(args) {
    return wrapEnvelope({
        plaintext: args.umk,
        kek: args.kek,
        kind: args.kind,
        userId: args.userId,
        envelopeId: args.envelopeId,
    });
}
/** Unwrap the UMK from an envelope blob. Auth failure → EnvelopeAuthTagMismatchError. */
export async function unwrapUmk(args) {
    const raw = await openEnvelope({
        blob: args.blob,
        kek: args.kek,
        expectedKind: args.expectedKind,
        userId: args.userId,
        envelopeId: args.envelopeId,
    });
    return brandUmk(raw);
}
/**
 * INV-14 — passphrase (or any KEK) rotation. Re-wraps the SAME UMK under a new
 * KEK in the SAME envelope row (same kind + envelopeId). Items are never touched.
 * The recovered UMK carrier is wiped before return.
 */
export async function rewrapEnvelope(args) {
    const umk = await unwrapUmk({
        blob: args.existingBlob,
        kek: args.oldKek,
        expectedKind: args.kind,
        userId: args.userId,
        envelopeId: args.envelopeId,
    });
    try {
        return await wrapUmk({
            umk,
            kek: args.newKek,
            kind: args.kind,
            userId: args.userId,
            envelopeId: args.envelopeId,
        });
    }
    finally {
        wipe(umk);
    }
}
/**
 * INV-13 — add a NEW envelope (e.g. `kind='mitid'`) for an existing UMK. Opens
 * the UMK via an existing envelope, re-seals it under a new KEK into a NEW
 * envelope row. Produces exactly one new `umk_envelopes` blob and zero item
 * bytes — the caller writes a single row; existing items are untouched.
 * The recovered UMK carrier is wiped before return.
 */
export async function addEnvelope(args) {
    const umk = await unwrapUmk({
        blob: args.existingBlob,
        kek: args.existingKek,
        expectedKind: args.existingKind,
        userId: args.userId,
        envelopeId: args.existingEnvelopeId,
    });
    try {
        return await wrapUmk({
            umk,
            kek: args.newKek,
            kind: args.newKind,
            userId: args.userId,
            envelopeId: args.newEnvelopeId,
        });
    }
    finally {
        wipe(umk);
    }
}
//# sourceMappingURL=umk.js.map